import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import Header from './Header';
import Footer from './Footer';
import StripePaymentForm from './StripePaymentForm';
import FlutterwavePaymentForm from './FlutterwavePaymentForm';
import { formatPrice, formatCurrency, formatFlightPrice, formatFlightPriceComponent, getCurrency, initializeCurrency, convertCurrency } from './utils/currency';
import './PaymentPage.css';

// Must match Supabase secret STRIPE_SECRET_KEY: pk_test_… + sk_test_… (test) or pk_live_… + sk_live_… (live)
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

const PaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get flight and passenger data from navigation state - REQUIRED, no fallback
  const flight = location.state?.flight;
  const passengerData = location.state?.passengerData || {};

  // Redirect if no flight data (should come from flight selection)
  React.useEffect(() => {
    if (!flight) {
      console.error('No flight data found. Redirecting to flight search...');
      navigate('/book-flight', { 
        replace: true,
        state: { error: 'Please select a flight first' }
      });
    }
  }, [flight, navigate]);
  const additionalOptions = location.state?.additionalOptions || {};

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('flutterwave');
  const [paymentDetails, setPaymentDetails] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    ussdCode: '',
    bankName: '',
    accountNumber: '',
    referenceNumber: ''
  });
  const [currency, setCurrency] = useState(null); // Currency based on location

  // Initialize currency on component mount
  useEffect(() => {
    const initCurrency = async () => {
      try {
        console.log('PaymentPage: Initializing currency...');
        const detectedCurrency = await initializeCurrency(true);
        setCurrency(detectedCurrency);
        console.log('PaymentPage: Currency initialized:', {
          code: detectedCurrency.code,
          symbol: detectedCurrency.symbol,
          name: detectedCurrency.name
        });
      } catch (error) {
        console.error('PaymentPage: Error initializing currency:', error);
        const fallbackCurrency = getCurrency();
        setCurrency(fallbackCurrency);
      }
    };
    initCurrency();

    // Listen for currency initialization from App
    const handleCurrencyInit = (event) => {
      console.log('PaymentPage: Received currency from App:', event.detail);
      setCurrency(event.detail);
    };
    
    window.addEventListener('currencyInitialized', handleCurrencyInit);
    return () => {
      window.removeEventListener('currencyInitialized', handleCurrencyInit);
    };
  }, []);

  const paymentMethods = [
    {
      id: 'flutterwave',
      name: 'Flutterwave',
      description: 'Card, Bank Transfer, USSD, Account',
      icon: '💳',
      methods: ['Card', 'Bank Transfer', 'USSD', 'Account']
    },
    {
      id: 'stripe',
      name: 'Stripe',
      description: 'Credit or Debit Card',
      icon: '💳',
      methods: ['Card']
    }
  ];

  const handlePaymentMethodSelect = (methodId) => {
    setSelectedPaymentMethod(methodId);
    // Reset payment details when method changes
    setPaymentDetails({
      cardNumber: '',
      expiryDate: '',
      cvv: '',
      ussdCode: '',
      bankName: '',
      accountNumber: '',
      referenceNumber: ''
    });
  };

  const handlePaymentDetailChange = (e) => {
    const { name, value } = e.target;
    setPaymentDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  const handleStripeSuccess = async (result) => {
    // Payment succeeded (Stripe or Flutterwave) - navigate to success page
    // Note: booking might be null if booking creation failed, but payment succeeded
    navigate('/success', {
      state: {
        booking: result.booking,
        payment: result.payment,
        flight: {
          ...flight,
          departureCity: flight.departureCity || (flight.departureAirport === 'LOS' ? 'Lagos' : 
                          flight.departureAirport === 'ABV' ? 'Abuja' : 
                          flight.departureAirport === 'LHR' ? 'London' : 
                          flight.departureAirport === 'DXB' ? 'Dubai' : 
                          flight.departureAirport === 'ACC' ? 'Accra' : 'City'),
          arrivalCity: flight.arrivalCity || (flight.arrivalAirport === 'LOS' ? 'Lagos' : 
                         flight.arrivalAirport === 'ABV' ? 'Abuja' : 
                         flight.arrivalAirport === 'LHR' ? 'London' : 
                         flight.arrivalAirport === 'DXB' ? 'Dubai' : 
                         flight.arrivalAirport === 'ACC' ? 'Accra' : 'City'),
          travelClass: flight.travelClass || 'Business',
          price: flight.total || flight.price,
          baseFare: flight.baseFare || '₦750,000',
          taxes: flight.taxes || '₦100,000',
          total: flight.total || flight.price
        },
        passengerData,
        additionalOptions,
        message: result.message || null,
        error: result.error || null
      }
    });
  };

  const handleStripeError = (error) => {
    setPaymentError(error);
    setProcessingPayment(false);
  };

  const handleOtherPayment = async (e) => {
    e.preventDefault();
    setProcessingPayment(true);
    setPaymentError(null);

    try {
      // For other payment methods (Paystack, Flutterwave, etc.)
      // Process payment first, then create booking
      
      // Simulate payment processing (replace with actual payment gateway)
      console.log('Processing payment:', {
        method: selectedPaymentMethod,
        details: paymentDetails,
        flight,
        passengerData
      });

      // After payment succeeds, create booking
      // CRITICAL: Use the offer ID from the flight object
      // This should be the selected class's offer ID (set in FlightDetailsPage)
      const bookingOfferId = flight.id || flight.offerId;
      console.log('🔵 PaymentPage - Using offer ID for booking:', bookingOfferId);
      console.log('🔵 PaymentPage - Flight travelClass:', flight.travelClass);
      console.log('🔵 PaymentPage - Flight cabinClass:', flight.cabinClass);
      
      if (bookingOfferId) {
        const bookingResponse = await fetch('http://localhost:3001/api/bookings/create', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            offerId: bookingOfferId,
            passengers: [{
              type: 'adult',
              given_name: passengerData?.firstName || (passengerData?.fullName ? passengerData.fullName.split(' ')[0] : 'John'),
              family_name: passengerData?.lastName || (passengerData?.fullName ? passengerData.fullName.split(' ').slice(1).join(' ') : 'Doe'),
              email: passengerData?.email || 'test@example.com',
              phone_number: passengerData?.phone || passengerData?.phoneNumber || '+2348012345678',
              title: passengerData?.title ? (() => {
                const titleLower = String(passengerData.title).toLowerCase().trim();
                if (titleLower === 'mr' || titleLower === 'mister') return 'mr';
                if (titleLower === 'mrs' || titleLower === 'missus') return 'mrs';
                if (titleLower === 'ms') return 'ms';
                if (titleLower === 'miss') return 'miss';
                return titleLower || 'mr';
              })() : 'mr',
              gender: passengerData?.gender ? (() => {
                const genderLower = String(passengerData.gender).toLowerCase().trim();
                // Duffel expects 'm' or 'f'
                if (genderLower === 'male' || genderLower === 'm' || genderLower === 'man') return 'm';
                if (genderLower === 'female' || genderLower === 'f' || genderLower === 'woman') return 'f';
                if (genderLower === 'm' || genderLower === 'f') return genderLower;
                return 'm'; // Default to 'm'
              })() : 'm',
              // Only include born_on if provided and valid (not in future)
              ...(passengerData?.dateOfBirth && (() => {
                const birthDate = new Date(passengerData.dateOfBirth);
                const today = new Date();
                today.setHours(23, 59, 59, 999);
                if (birthDate <= today) {
                  return { born_on: passengerData.dateOfBirth };
                }
                return {}; // Don't include if invalid
              })())
            }],
            contacts: [{
              email: passengerData?.email || 'test@example.com',
              phone_number: passengerData?.phone || '+2348012345678'
            }],
            paymentType: 'balance' // Use balance for test mode with other gateways
          }),
        });

        const bookingResult = await bookingResponse.json();

        if (bookingResult.success) {
          navigate('/success', {
            state: {
              booking: bookingResult.booking,
              payment: {
                method: selectedPaymentMethod,
                status: 'success'
              },
              flight: {
                ...flight,
                departureCity: flight.departureCity || (flight.departureAirport === 'LOS' ? 'Lagos' : 
                                flight.departureAirport === 'ABV' ? 'Abuja' : 
                                flight.departureAirport === 'LHR' ? 'London' : 
                                flight.departureAirport === 'DXB' ? 'Dubai' : 
                                flight.departureAirport === 'ACC' ? 'Accra' : 'City'),
                arrivalCity: flight.arrivalCity || (flight.arrivalAirport === 'LOS' ? 'Lagos' : 
                               flight.arrivalAirport === 'ABV' ? 'Abuja' : 
                               flight.arrivalAirport === 'LHR' ? 'London' : 
                               flight.arrivalAirport === 'DXB' ? 'Dubai' : 
                               flight.arrivalAirport === 'ACC' ? 'Accra' : 'City'),
                travelClass: flight.travelClass || 'Business',
                price: flight.total || flight.price,
                baseFare: flight.baseFare || '₦750,000',
                taxes: flight.taxes || '₦100,000',
                total: flight.total || flight.price
              },
              passengerData,
              additionalOptions
            }
          });
        } else {
          setPaymentError('Booking failed: ' + (bookingResult.error || 'Unknown error'));
        }
      } else {
        // Navigate to success page (for testing without real booking)
        navigate('/success', {
          state: {
            flight: {
              ...flight,
              departureCity: flight.departureCity || (flight.departureAirport === 'LOS' ? 'Lagos' : 
                              flight.departureAirport === 'ABV' ? 'Abuja' : 
                              flight.departureAirport === 'LHR' ? 'London' : 
                              flight.departureAirport === 'DXB' ? 'Dubai' : 
                              flight.departureAirport === 'ACC' ? 'Accra' : 'City'),
              arrivalCity: flight.arrivalCity || (flight.arrivalAirport === 'LOS' ? 'Lagos' : 
                             flight.arrivalAirport === 'ABV' ? 'Abuja' : 
                             flight.arrivalAirport === 'LHR' ? 'London' : 
                             flight.arrivalAirport === 'DXB' ? 'Dubai' : 
                             flight.arrivalAirport === 'ACC' ? 'Accra' : 'City'),
              travelClass: flight.travelClass || 'Business',
              price: flight.total || flight.price,
              baseFare: flight.baseFare || '₦750,000',
              taxes: flight.taxes || '₦100,000',
              total: flight.total || flight.price
            },
            passengerData,
            additionalOptions
          }
        });
      }
    } catch (error) {
      setPaymentError('Payment processing failed: ' + error.message);
    } finally {
      setProcessingPayment(false);
    }
  };

  // Helper function to extract numeric value from formatted price string
  const extractNumericValue = (priceValue) => {
    if (!priceValue) return 0;
    if (typeof priceValue === 'number') return priceValue;
    if (typeof priceValue === 'string') {
      return parseFloat(priceValue.replace(/[₦,$£€,]/g, '')) || 0;
    }
    return 0;
  };

  // Get raw numeric total for payment processing (amounts in user's display currency)
  const getNumericTotal = () => {
    let baseFareNum = 0;
    let taxesNum = 0;

    if (flight.baseFare) {
      baseFareNum = extractNumericValue(flight.baseFare);
    } else if (flight.price?.base != null && typeof flight.price === 'object') {
      baseFareNum = extractNumericValue(formatFlightPriceComponent(flight.price.base, flight.price));
    }

    if (flight.taxes) {
      taxesNum = extractNumericValue(flight.taxes);
    } else if (flight.price?.taxes != null && typeof flight.price === 'object') {
      taxesNum = extractNumericValue(formatFlightPriceComponent(flight.price.taxes, flight.price));
    }

    let total = baseFareNum + taxesNum;
    if (total <= 0 && flight.price && typeof flight.price === 'object') {
      total = extractNumericValue(formatFlightPrice(flight.price));
    }
    if (total <= 0 && flight.total) {
      total = extractNumericValue(flight.total);
    }
    if (total <= 0 && flight.price && typeof flight.price === 'string') {
      total = extractNumericValue(flight.price);
    }

    if (additionalOptions.extraBaggage) total += extractNumericValue(formatPrice(15000));
    if (additionalOptions.seatSelection) total += extractNumericValue(formatPrice(5000));
    if (additionalOptions.tripInsurance) total += extractNumericValue(formatPrice(5000));

    return total;
  };

  const calculateTotal = () => {
    const n = getNumericTotal();
    return formatCurrency(n, { currency: getCurrency() });
  };

  if (!flight) {
    return (
      <div className="payment-page">
        <Header />
        <p style={{ padding: '2rem', textAlign: 'center' }}>Loading…</p>
        <Footer />
      </div>
    );
  }

  return (
    <div className="payment-page">
      <Header />
      
      {/* Flight Summary Card */}
      <section className="payment-summary-section">
        <div className="page-container">
          <div className="payment-summary-card">
            <div className="summary-header">
              <div className="summary-airline">
                <span className="summary-logo">{flight.airlineLogo}</span>
                <div>
                  <h3 className="summary-airline-name">{flight.airline}</h3>
                  <p className="summary-route-text">
                    {flight.departureAirport} → {flight.arrivalAirport}
                  </p>
                </div>
              </div>
            </div>

            {/* Multi-City Routes */}
            {flight.isMultiCity && flight.slices && flight.slices.length > 0 && (
              <>
                {flight.slices.map((slice, sliceIndex) => {
                  const firstSegment = slice.segments?.[0];
                  const lastSegment = slice.segments?.[slice.segments?.length - 1];
                  
                  const formatTime = (isoString) => {
                    if (!isoString) return '';
                    try {
                      const date = new Date(isoString);
                      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
                    } catch {
                      return '';
                    }
                  };
                  
                  const formatDate = (isoString) => {
                    if (!isoString) return 'Date';
                    try {
                      const date = new Date(isoString);
                      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                    } catch {
                      return 'Date';
                    }
                  };
                  
                  const formatDuration = (duration) => {
                    if (!duration) return 'N/A';
                    if (typeof duration === 'string' && duration.startsWith('PT')) {
                      const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
                      if (match) {
                        const hours = match[1] || '0';
                        const minutes = match[2] || '0';
                        if (hours === '0') return `${minutes}m`;
                        if (minutes === '0') return `${hours}h`;
                        return `${hours}h ${minutes}m`;
                      }
                    }
                    return duration;
                  };
                  
                  if (!firstSegment || !lastSegment) return null;
                  
                  return (
                    <div key={sliceIndex} className="summary-details" style={{ marginBottom: sliceIndex < flight.slices.length - 1 ? '15px' : '0' }}>
                      <div className="summary-flight-label">Route {sliceIndex + 1} of {flight.slices.length}</div>
                      <div className="summary-time-info">
                        <div className="time-block">
                          <span className="time-label">Departure</span>
                          <span className="time-value">{formatTime(firstSegment.departing_at)}</span>
                          <span className="time-date">{formatDate(firstSegment.departing_at)}</span>
                          <span className="time-airport">{firstSegment.origin?.iata_code || 'N/A'}</span>
                        </div>
                        <div className="duration-block">
                          <span className="duration-value">{formatDuration(slice.duration)}</span>
                          <span className="duration-class">{flight.travelClass || 'Business'}</span>
                        </div>
                        <div className="time-block">
                          <span className="time-label">Arrival</span>
                          <span className="time-value">{formatTime(lastSegment.arriving_at)}</span>
                          <span className="time-date">{formatDate(lastSegment.arriving_at)}</span>
                          <span className="time-airport">{lastSegment.destination?.iata_code || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            )}

            {/* Outbound Flight (One-way or Round-trip) */}
            {!flight.isMultiCity && (
              <div className="summary-details">
                <div className="summary-flight-label">Outbound</div>
                <div className="summary-time-info">
                  <div className="time-block">
                    <span className="time-label">Departure</span>
                    <span className="time-value">{flight.departureTime}</span>
                    <span className="time-date">
                      {flight.departureDateFormatted || 
                       (flight.departureDate ? (() => {
                         try {
                           const date = new Date(flight.departureDate);
                           return !isNaN(date.getTime()) ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Date';
                         } catch (e) {
                           return 'Date';
                         }
                       })() : 'Date')}
                    </span>
                    <span className="time-airport" style={{ fontWeight: 600 }}>{flight.departureAirport}</span>
                    <span className="time-city" style={{ fontSize: '0.85rem', color: '#666' }}>{flight.departureCity}</span>
                  </div>
                  <div className="duration-block">
                    <span className="duration-value">{flight.durationFormatted || flight.duration}</span>
                    <span className="duration-class">{flight.travelClass || flight.cabinClass || 'Economy'}</span>
                  </div>
                  <div className="time-block">
                    <span className="time-label">Arrival</span>
                    <span className="time-value">{flight.arrivalTime}</span>
                    <span className="time-date">
                      {flight.arrivalDateFormatted || 
                       (flight.arrivalDate ? new Date(flight.arrivalDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 
                       'Date')}
                    </span>
                    <span className="time-airport" style={{ fontWeight: 600 }}>{flight.arrivalAirport}</span>
                    <span className="time-city" style={{ fontSize: '0.85rem', color: '#666' }}>{flight.arrivalCity}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Return Flight (if round-trip, NOT multi-city) */}
            {flight.isRoundTrip && !flight.isMultiCity && flight.returnFlight && (
              <div className="summary-details return-flight-details">
                <div className="summary-flight-label return-label">Return</div>
                <div className="summary-time-info">
                  <div className="time-block">
                    <span className="time-label">Departure</span>
                    <span className="time-value">{flight.returnFlight.departureTime}</span>
                    <span className="time-date">
                      {(() => {
                        try {
                          if (flight.return?.segments?.[0]?.departing_at) {
                            const date = new Date(flight.return.segments[0].departing_at);
                            if (!isNaN(date.getTime())) {
                              return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                            }
                          }
                          return 'Date';
                        } catch (e) {
                          return 'Date';
                        }
                      })()}
                    </span>
                  </div>
                  <div className="duration-block">
                    <span className="duration-value">{flight.returnFlight.duration}</span>
                    <span className="duration-class">{flight.travelClass || 'Business'}</span>
                  </div>
                  <div className="time-block">
                    <span className="time-label">Arrival</span>
                    <span className="time-value">{flight.returnFlight.arrivalTime}</span>
                    <span className="time-date">
                      {(() => {
                        try {
                          if (flight.return?.segments && flight.return.segments.length > 0) {
                            const lastSegment = flight.return.segments[flight.return.segments.length - 1];
                            if (lastSegment?.arriving_at) {
                              const date = new Date(lastSegment.arriving_at);
                              if (!isNaN(date.getTime())) {
                                return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                              }
                            }
                          }
                          return 'Date';
                        } catch (e) {
                          return 'Date';
                        }
                      })()}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="summary-price-breakdown">
              <div className="price-row">
                <span className="price-label">Base Fare</span>
                <span className="price-value">
                  {(() => {
                    // Priority 1: Use flight.baseFare if it's a formatted string (same as details page)
                    if (flight.baseFare && typeof flight.baseFare === 'string' && flight.baseFare.trim() !== '') {
                      return flight.baseFare;
                    }
                    // Priority 2: Use flight.total or flight.price (already formatted from previous pages)
                    if (flight.total && typeof flight.total === 'string') {
                      // If total is a formatted string, use it as base fare
                      return flight.total;
                    }
                    if (flight.price && typeof flight.price === 'string') {
                      return flight.price;
                    }
                    if (flight.price?.base != null && typeof flight.price === 'object') {
                      return formatFlightPriceComponent(flight.price.base, flight.price);
                    }
                    let totalPrice = 0;
                    if (flight.price?.total) {
                      totalPrice = parseFloat(flight.price.total) || 0;
                    } else if (flight.total && typeof flight.total === 'number') {
                      totalPrice = flight.total;
                    } else if (flight.price && typeof flight.price === 'object' && flight.price.total) {
                      totalPrice = parseFloat(flight.price.total) || 0;
                    }
                    if (totalPrice > 0) {
                      return formatFlightPrice(flight.price || { total: totalPrice, currency: 'NGN' });
                    }
                    return formatCurrency(0, { currency: getCurrency() });
                  })()}
                </span>
              </div>
              <div className="price-row">
                <span className="price-label">Taxes & Fees</span>
                <span className="price-value">
                  {(() => {
                    // Priority 1: Use flight.taxes if it's a formatted string (same as details page)
                    if (flight.taxes && typeof flight.taxes === 'string' && flight.taxes.trim() !== '') {
                      return flight.taxes;
                    }
                    // Priority 2: If we have baseFare and total as strings, calculate taxes
                    if (flight.baseFare && typeof flight.baseFare === 'string' && 
                        (flight.total || flight.price) && typeof (flight.total || flight.price) === 'string') {
                      // Try to extract numeric values and calculate difference
                      const baseNum = parseFloat(flight.baseFare.replace(/[₦,$£€,]/g, '')) || 0;
                      const totalStr = flight.total || flight.price;
                      const totalNum = parseFloat(totalStr.replace(/[₦,$£€,]/g, '')) || 0;
                      if (totalNum > baseNum) {
                        return formatPrice(totalNum - baseNum);
                      }
                    }
                    // Priority 3: Check if we have taxes as a number (from API)
                    if (flight.taxes && typeof flight.taxes === 'number' && flight.taxes > 0) {
                      return formatPrice(flight.taxes);
                    }
                    if (flight.price?.taxes != null && typeof flight.price === 'object') {
                      return formatFlightPriceComponent(flight.price.taxes, flight.price);
                    }
                    return formatCurrency(0, { currency: getCurrency() });
                  })()}
                </span>
              </div>
              {additionalOptions.extraBaggage && (
                <div className="price-row">
                  <span className="price-label">Extra Baggage</span>
                  <span className="price-value">{formatPrice(15000)}</span>
                </div>
              )}
              {additionalOptions.seatSelection && (
                <div className="price-row">
                  <span className="price-label">Seat Selection</span>
                  <span className="price-value">{formatPrice(5000)}</span>
                </div>
              )}
              {additionalOptions.tripInsurance && (
                <div className="price-row">
                  <span className="price-label">Trip Insurance</span>
                  <span className="price-value">{formatPrice(5000)}</span>
                </div>
              )}
              <div className="price-divider"></div>
              <div className="price-row total">
                <span className="price-label">Total Amount</span>
                <span className="price-value">
                  {(() => {
                    // Extract numeric values from displayed Base Fare and Taxes (already in target currency)
                    const baseFareNum = extractNumericValue(flight.baseFare);
                    const taxesNum = extractNumericValue(flight.taxes);
                    let total = baseFareNum + taxesNum;
                    if (total <= 0 && flight.price && typeof flight.price === 'object' && flight.price.total != null) {
                      return formatFlightPrice(flight.price);
                    }
                    if (additionalOptions.extraBaggage) {
                      total += extractNumericValue(formatPrice(15000));
                    }
                    if (additionalOptions.seatSelection) {
                      total += extractNumericValue(formatPrice(5000));
                    }
                    if (additionalOptions.tripInsurance) {
                      total += extractNumericValue(formatPrice(5000));
                    }
                    const currentCurrency = getCurrency();
                    return formatCurrency(total, { currency: currentCurrency });
                  })()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Payment Section */}
      <section className="payment-main-section">
        <div className="page-container">
          <div className="payment-layout">
            {/* Payment Method Selection */}
            <div className="payment-methods-panel">
              <h2 className="payment-section-title">Select Payment Method</h2>
              <div className="payment-methods-grid">
                {paymentMethods.map(method => (
                  <div
                    key={method.id}
                    className={`payment-method-card ${selectedPaymentMethod === method.id ? 'selected' : ''}`}
                    onClick={() => handlePaymentMethodSelect(method.id)}
                  >
                    <div className="method-icon">{method.icon}</div>
                    <div className="method-info">
                      <h4 className="method-name">{method.name}</h4>
                      <p className="method-description">{method.description}</p>
                    </div>
                    <div className="method-check">
                      {selectedPaymentMethod === method.id && (
                        <span className="check-mark">✓</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Payment Details Form */}
            {selectedPaymentMethod && (
              <div className="payment-details-panel">
                <h2 className="payment-section-title">Payment Details</h2>
                
                {/* Flutterwave Payment Form */}
                {selectedPaymentMethod === 'flutterwave' && (
                  <FlutterwavePaymentForm
                    amount={(() => {
                      // Calculate total in user's currency first (for display)
                      const baseFareNum = extractNumericValue(flight.baseFare);
                      const taxesNum = extractNumericValue(flight.taxes);
                      let totalInUserCurrency = baseFareNum + taxesNum;
                      
                      // Add additional options (convert from NGN to user currency)
                      if (additionalOptions.extraBaggage) {
                        const baggageInTargetCurrency = formatPrice(15000);
                        totalInUserCurrency += extractNumericValue(baggageInTargetCurrency);
                      }
                      if (additionalOptions.seatSelection) {
                        const seatInTargetCurrency = formatPrice(5000);
                        totalInUserCurrency += extractNumericValue(seatInTargetCurrency);
                      }
                      if (additionalOptions.tripInsurance) {
                        const insuranceInTargetCurrency = formatPrice(5000);
                        totalInUserCurrency += extractNumericValue(insuranceInTargetCurrency);
                      }
                      
                      // Convert from user's currency back to NGN for Flutterwave
                      const currentCurrency = getCurrency();
                      let totalInNGN = 0;
                      
                      if (currentCurrency.code === 'NGN') {
                        // Already in NGN, no conversion needed
                        totalInNGN = totalInUserCurrency;
                      } else {
                        // Convert from user currency to NGN
                        totalInNGN = convertCurrency(totalInUserCurrency, currentCurrency.code, 'NGN');
                      }
                      
                      // Return formatted NGN amount for Flutterwave
                      return formatCurrency(totalInNGN, { currency: { code: 'NGN', symbol: '₦', name: 'Nigerian Naira' } });
                    })()}
                    flight={flight}
                    passengerData={passengerData}
                    onSuccess={handleStripeSuccess}
                    onError={handleStripeError}
                  />
                )}

                {/* Stripe Payment Form */}
                {selectedPaymentMethod === 'stripe' && !stripePromise && (
                  <div className="payment-error">
                    <p>
                      Stripe publishable key is missing. Add{' '}
                      <code>REACT_APP_STRIPE_PUBLISHABLE_KEY=pk_…</code> to <code>.env</code>, restart the dev
                      server, and use the same Stripe account as your Supabase <code>STRIPE_SECRET_KEY</code>{' '}
                      (pair <code>pk_test</code> with <code>sk_test</code>, or <code>pk_live</code> with <code>sk_live</code>).
                    </p>
                  </div>
                )}
                {selectedPaymentMethod === 'stripe' && stripePromise && (
                  <Elements stripe={stripePromise}>
                    <StripePaymentForm
                      amount={(() => {
                        // Calculate total in user's currency for display
                        const baseFareNum = extractNumericValue(flight.baseFare);
                        const taxesNum = extractNumericValue(flight.taxes);
                        let totalInUserCurrency = baseFareNum + taxesNum;

                        // Add additional options (convert from NGN to user currency)
                        if (additionalOptions.extraBaggage) {
                          const baggageInTargetCurrency = formatPrice(15000);
                          totalInUserCurrency += extractNumericValue(baggageInTargetCurrency);
                        }
                        if (additionalOptions.seatSelection) {
                          const seatInTargetCurrency = formatPrice(5000);
                          totalInUserCurrency += extractNumericValue(seatInTargetCurrency);
                        }
                        if (additionalOptions.tripInsurance) {
                          const insuranceInTargetCurrency = formatPrice(5000);
                          totalInUserCurrency += extractNumericValue(insuranceInTargetCurrency);
                        }

                        const currentCurrency = getCurrency();
                        return formatCurrency(totalInUserCurrency, { currency: currentCurrency });
                      })()}
                      amountNGN={(() => {
                        // Calculate total in user's currency first
                        const baseFareNum = extractNumericValue(flight.baseFare);
                        const taxesNum = extractNumericValue(flight.taxes);
                        let totalInUserCurrency = baseFareNum + taxesNum;

                        if (additionalOptions.extraBaggage) {
                          const baggageInTargetCurrency = formatPrice(15000);
                          totalInUserCurrency += extractNumericValue(baggageInTargetCurrency);
                        }
                        if (additionalOptions.seatSelection) {
                          const seatInTargetCurrency = formatPrice(5000);
                          totalInUserCurrency += extractNumericValue(seatInTargetCurrency);
                        }
                        if (additionalOptions.tripInsurance) {
                          const insuranceInTargetCurrency = formatPrice(5000);
                          totalInUserCurrency += extractNumericValue(insuranceInTargetCurrency);
                        }

                        // Convert from user's currency back to NGN for Stripe charge
                        const currentCurrency = getCurrency();
                        if (currentCurrency.code === 'NGN') {
                          return totalInUserCurrency;
                        }
                        return convertCurrency(totalInUserCurrency, currentCurrency.code, 'NGN');
                      })()}
                      flight={flight}
                      passengerData={passengerData}
                      onSuccess={handleStripeSuccess}
                      onError={handleStripeError}
                    />
                  </Elements>
                )}

                {/* Other Payment Methods */}
                {selectedPaymentMethod !== 'stripe' && selectedPaymentMethod !== 'flutterwave' && (
                  <form onSubmit={handleOtherPayment} className="payment-form">
                    {paymentError && (
                      <div className="payment-error">
                        <p>{paymentError}</p>
                      </div>
                    )}

                    {/* Card Payment Fields */}
                    {(selectedPaymentMethod === 'paystack' || 
                      selectedPaymentMethod === 'flutterwave' || 
                      selectedPaymentMethod === 'verve') && (
                    <>
                      <div className="form-group">
                        <label htmlFor="cardNumber" className="form-label">
                          Card Number <span className="required">*</span>
                        </label>
                        <input
                          type="text"
                          id="cardNumber"
                          name="cardNumber"
                          value={paymentDetails.cardNumber}
                          onChange={handlePaymentDetailChange}
                          placeholder="1234 5678 9012 3456"
                          className="form-input"
                          maxLength="19"
                          required
                        />
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="expiryDate" className="form-label">
                            Expiry Date <span className="required">*</span>
                          </label>
                          <input
                            type="text"
                            id="expiryDate"
                            name="expiryDate"
                            value={paymentDetails.expiryDate}
                            onChange={handlePaymentDetailChange}
                            placeholder="MM/YY"
                            className="form-input"
                            maxLength="5"
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="cvv" className="form-label">
                            CVV <span className="required">*</span>
                          </label>
                          <input
                            type="text"
                            id="cvv"
                            name="cvv"
                            value={paymentDetails.cvv}
                            onChange={handlePaymentDetailChange}
                            placeholder="123"
                            className="form-input"
                            maxLength="4"
                            required
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* USSD Payment Fields */}
                  {selectedPaymentMethod === 'ussd' && (
                    <>
                      <div className="ussd-instructions">
                        <p className="instruction-text">
                          Dial the USSD code below on your phone to complete payment:
                        </p>
                        <div className="ussd-code-display">
                          <span className="ussd-code">*737*{Math.round(getNumericTotal()) || '850000'}#</span>
                        </div>
                        <p className="instruction-note">
                          After dialing, enter the transaction reference below:
                        </p>
                      </div>

                      <div className="form-group">
                        <label htmlFor="ussdCode" className="form-label">
                          Transaction Reference <span className="required">*</span>
                        </label>
                        <input
                          type="text"
                          id="ussdCode"
                          name="ussdCode"
                          value={paymentDetails.ussdCode}
                          onChange={handlePaymentDetailChange}
                          placeholder="Enter reference from USSD"
                          className="form-input"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="bankName" className="form-label">
                          Bank <span className="required">*</span>
                        </label>
                        <select
                          id="bankName"
                          name="bankName"
                          value={paymentDetails.bankName}
                          onChange={handlePaymentDetailChange}
                          className="form-select"
                          required
                        >
                          <option value="">Select Bank</option>
                          <option value="GTBank">GTBank</option>
                          <option value="Zenith Bank">Zenith Bank</option>
                          <option value="Access Bank">Access Bank</option>
                          <option value="First Bank">First Bank</option>
                          <option value="UBA">UBA</option>
                          <option value="Fidelity Bank">Fidelity Bank</option>
                        </select>
                      </div>
                    </>
                  )}

                  {/* Bank Transfer Fields */}
                  {selectedPaymentMethod === 'bank-transfer' && (
                    <>
                      <div className="bank-transfer-info">
                        <p className="info-text">
                          Transfer the exact amount to the account details below:
                        </p>
                        <div className="account-details">
                          <div className="account-row">
                            <span className="account-label">Bank:</span>
                            <span className="account-value">SymbolFly Bank</span>
                          </div>
                          <div className="account-row">
                            <span className="account-label">Account Number:</span>
                            <span className="account-value">1234567890</span>
                          </div>
                          <div className="account-row">
                            <span className="account-label">Account Name:</span>
                            <span className="account-value">SymbolFly Limited</span>
                          </div>
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="referenceNumber" className="form-label">
                          Transfer Reference <span className="required">*</span>
                        </label>
                        <input
                          type="text"
                          id="referenceNumber"
                          name="referenceNumber"
                          value={paymentDetails.referenceNumber}
                          onChange={handlePaymentDetailChange}
                          placeholder="Enter transfer reference"
                          className="form-input"
                          required
                        />
                      </div>
                    </>
                  )}

                    {/* Security Strip */}
                    <div className="security-strip">
                      <div className="security-icons">
                        <span className="security-icon">🔒</span>
                        <span className="security-icon">🛡️</span>
                        <span className="security-icon">✓</span>
                      </div>
                      <p className="security-text">
                        Secure payments powered by Stripe
                      </p>
                    </div>

                    {/* Pay Now Button */}
                    <button type="submit" className="pay-now-button">
                      Pay {calculateTotal()}
                    </button>
                  </form>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PaymentPage;

